import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Plus,
  Phone,
  Mail,
  Calendar,
  User,
  ExternalLink,
  Sparkles,
  Zap,
  ClipboardCheck,
  Stethoscope,
  ShieldCheck,
  ShieldX,
  ChevronRight,
  X,
  MapPin,
  Briefcase,
  FileText,
  CreditCard,
  ScrollText,
  ShoppingBag,
} from 'lucide-react';
import type {
  Client,
  ConsentType,
  SessionType,
  SessionStatus,
  PurchasedProduct,
} from 'shared';
import { api, ApiError } from '@/lib/api';
import { AppShell } from '@/components/layout/AppShell';
import { Header } from '@/components/layout/Header';
import { Avatar } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Skeleton, SkeletonCard } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';

/* ==========================================================================
   S3 — Client Profile

   Header with avatar + essential data, consent badges,
   "+ Nuovo Check-up" CTA, timeline of sessions.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

interface ClientDetail extends Client {
  createdAt?: string;
}

interface ConsentRecord {
  id: number;
  clientId: number;
  consentType: ConsentType;
  granted: boolean;
  revokedAt: string | null;
  grantedAt: string;
}

interface CheckupSession {
  id: number;
  clientId: number;
  sessionType: SessionType;
  status: SessionStatus;
  operatriceName?: string;
  createdAt: string;
  completedAt?: string | null;
}

interface LaserSession {
  id: number;
  clientId: number;
  sedutaNumber: number;
  zone: string;
  parametersSummary?: string;
  operatriceName?: string;
  createdAt: string;
}

type TimelineEntry =
  | { kind: 'checkup'; data: CheckupSession }
  | { kind: 'laser'; data: LaserSession };

/* --------------------------------------------------------------------------
   Constants
   -------------------------------------------------------------------------- */

const SESSION_TYPE_LABELS: Record<SessionType, string> = {
  welcome: 'Welcome Checkup',
  beauty: 'Beauty Checkup',
  kinesiology: 'Kinesiologia',
  medical: 'Medico Estetico',
};

const SESSION_TYPE_ICONS: Record<SessionType, typeof Sparkles> = {
  welcome: ClipboardCheck,
  beauty: Sparkles,
  kinesiology: Stethoscope,
  medical: Stethoscope,
};

const SESSION_TYPE_DESCRIPTIONS: Record<SessionType, string> = {
  welcome: 'Prima visita e accoglienza',
  beauty: 'Analisi viso e corpo',
  kinesiology: 'Analisi posturale e muscolare',
  medical: 'Valutazione medico estetica',
};

const CONSENT_LABELS: Record<ConsentType, string> = {
  trattamento_dati: 'Dati',
  foto: 'Foto',
  laser: 'Laser',
  marketing: 'Marketing',
};

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function ClientProfile() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [client, setClient] = useState<ClientDetail | null>(null);
  const [consents, setConsents] = useState<ConsentRecord[]>([]);
  const [checkups, setCheckups] = useState<CheckupSession[]>([]);
  const [laserSessions, setLaserSessions] = useState<LaserSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNewCheckupModal, setShowNewCheckupModal] = useState(false);
  // Add memo modal
  const [showMemoModal, setShowMemoModal] = useState(false);
  const [memoText, setMemoText] = useState('');
  const [memoSubmitting, setMemoSubmitting] = useState(false);
  const [memoError, setMemoError] = useState<string | null>(null);
  // Edit anagrafica modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState<Partial<ClientDetail>>({});
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // ------------------------------------------------------------------
  // Fetch data
  // ------------------------------------------------------------------

  const loadData = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);

    try {
      const [clientData, consentsData, checkupsData] = await Promise.all([
        api.get<ClientDetail>(`/clients/${id}`),
        api.get<ConsentRecord[]>(`/consents/client/${id}`).catch(() => []),
        api.get<CheckupSession[]>(`/checkups/client/${id}`).catch(() => []),
      ]);

      setClient(clientData);
      setConsents(consentsData);
      setCheckups(checkupsData);

      // Laser sessions may come from a separate endpoint
      try {
        const laser = await api.get<LaserSession[]>(`/laser/client/${id}`);
        setLaserSessions(laser);
      } catch {
        setLaserSessions([]);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.status === 404 ? 'Cliente non trovata.' : err.message);
      } else {
        setError('Errore di connessione. Riprova.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ------------------------------------------------------------------
  // Build timeline
  // ------------------------------------------------------------------

  const timeline: TimelineEntry[] = [
    ...checkups.map((c): TimelineEntry => ({ kind: 'checkup', data: c })),
    ...laserSessions.map((l): TimelineEntry => ({ kind: 'laser', data: l })),
  ].sort((a, b) => {
    const dateA = new Date(a.data.createdAt).getTime();
    const dateB = new Date(b.data.createdAt).getTime();
    return dateB - dateA; // Newest first
  });

  // ------------------------------------------------------------------
  // Consent helpers
  // ------------------------------------------------------------------

  const getConsentStatus = (type: ConsentType): boolean => {
    const record = consents.find(
      (c) => c.consentType === type && c.granted && !c.revokedAt,
    );
    return Boolean(record);
  };

  // ------------------------------------------------------------------
  // Format helpers
  // ------------------------------------------------------------------

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '—';
    try {
      return new Intl.DateTimeFormat('it-IT', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }).format(new Date(dateStr));
    } catch {
      return '—';
    }
  };

  const formatSesso = (sesso: 'M' | 'F' | null): string => {
    if (sesso === 'F') return 'Donna';
    if (sesso === 'M') return 'Uomo';
    return '—';
  };

  // ------------------------------------------------------------------
  // Add memo handler — POST /api/clients/:id/memo (push verso dbgym)
  // ------------------------------------------------------------------

  const submitMemo = useCallback(async () => {
    if (!id || !memoText.trim()) return;
    setMemoSubmitting(true);
    setMemoError(null);
    try {
      const res = await api.post<{ ok: boolean; linea: string }>(
        `/clients/${id}/memo`,
        { descrizione: memoText.trim() },
      );
      if (res.ok) {
        // Refresh local: prepend la nuova linea al campo extra_memo del client in stato
        setClient((prev) =>
          prev
            ? {
                ...prev,
                extraMemo: prev.extraMemo ? `${res.linea}\n${prev.extraMemo}` : res.linea,
              }
            : prev,
        );
        setMemoText('');
        setShowMemoModal(false);
      }
    } catch (err) {
      setMemoError(
        err instanceof ApiError
          ? err.message
          : 'Errore salvataggio memo — riprova',
      );
    } finally {
      setMemoSubmitting(false);
    }
  }, [id, memoText]);

  // ------------------------------------------------------------------
  // Edit anagrafica handler — PATCH /api/clients/:id
  // ------------------------------------------------------------------

  const openEditModal = useCallback(() => {
    if (!client) return;
    setEditForm({
      nome: client.nome,
      cognome: client.cognome,
      telefono: client.telefono,
      email: client.email,
      sesso: client.sesso,
      professione: client.professione,
      indirizzoVia: client.indirizzoVia,
      indirizzoCitta: client.indirizzoCitta,
      indirizzoCap: client.indirizzoCap,
      indirizzoProvincia: client.indirizzoProvincia,
      fonte: client.fonte,
      autorizzaSms: client.autorizzaSms,
      autorizzaEmail: client.autorizzaEmail,
      autorizzaCartaceo: client.autorizzaCartaceo,
      noteInterne: client.noteInterne,
    });
    setEditError(null);
    setShowEditModal(true);
  }, [client]);

  const submitEdit = useCallback(async () => {
    if (!id) return;
    setEditSubmitting(true);
    setEditError(null);
    try {
      const updated = await api.patch<ClientDetail>(`/clients/${id}`, editForm);
      setClient((prev) => (prev ? { ...prev, ...updated } : prev));
      setShowEditModal(false);
    } catch (err) {
      setEditError(
        err instanceof ApiError ? err.message : 'Errore salvataggio',
      );
    } finally {
      setEditSubmitting(false);
    }
  }, [id, editForm]);

  // ------------------------------------------------------------------
  // Loading skeleton
  // ------------------------------------------------------------------

  if (isLoading) {
    return (
      <AppShell header={<Header showBack title="Profilo Cliente" />}>
        <div className="space-y-6">
          {/* Avatar + name skeleton */}
          <div className="flex items-center gap-4">
            <Skeleton circle width={80} height={80} />
            <div className="flex-1 space-y-2">
              <Skeleton height={24} className="w-3/5" />
              <Skeleton height={14} className="w-2/5" />
            </div>
          </div>
          {/* Info skeleton */}
          <div className="grid grid-cols-2 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} height={48} className="rounded-[var(--radius-md)]" />
            ))}
          </div>
          {/* Consent badges skeleton */}
          <div className="flex gap-2">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} height={32} className="w-20 rounded-[var(--radius-full)]" />
            ))}
          </div>
          {/* CTA skeleton */}
          <Skeleton height={56} className="rounded-[var(--radius-md)]" />
          {/* Timeline skeleton */}
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </div>
      </AppShell>
    );
  }

  // ------------------------------------------------------------------
  // Error state
  // ------------------------------------------------------------------

  if (error || !client) {
    return (
      <AppShell header={<Header showBack title="Profilo Cliente" />}>
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div
            className={cn(
              'mb-5 flex h-16 w-16 items-center justify-center',
              'rounded-full bg-status-negative-light',
            )}
          >
            <User className="h-7 w-7 text-status-negative" strokeWidth={1.5} />
          </div>
          <p className="text-lg font-[family-name:var(--font-heading)] font-semibold text-text-primary">
            {error ?? 'Cliente non trovata'}
          </p>
          <p className="mt-2 text-sm text-text-muted">
            Torna indietro e riprova
          </p>
          <Button
            variant="secondary"
            size="md"
            className="mt-6"
            onClick={() => navigate(-1)}
          >
            Torna indietro
          </Button>
        </div>
      </AppShell>
    );
  }

  // ------------------------------------------------------------------
  // Main render
  // ------------------------------------------------------------------

  const fullName = `${client.nome} ${client.cognome}`;
  const isExternal = Boolean(client.externalId);

  return (
    <AppShell header={<Header showBack title={fullName} />}>
      {/* ---- Header section ---- */}
      <section className="mb-6">
        <div className="flex items-start gap-5">
          <Avatar name={fullName} size="xl" />
          <div className="flex-1 min-w-0 pt-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-[family-name:var(--font-heading)] text-2xl font-extrabold text-text-primary tracking-tight">
                {fullName}
              </h2>
              {isExternal && (
                <Badge status="info" size="sm">
                  <ExternalLink className="h-3 w-3" />
                  Sincronizzato da CRM
                </Badge>
              )}
            </div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={openEditModal}
            className="shrink-0"
          >
            Modifica
          </Button>
        </div>

        {/* Essential data grid */}
        <div className="mt-5 grid grid-cols-2 gap-3">
          <InfoItem
            icon={<Phone className="h-4 w-4" strokeWidth={1.5} />}
            label="Telefono"
            value={client.telefono ?? '—'}
          />
          <InfoItem
            icon={<Mail className="h-4 w-4" strokeWidth={1.5} />}
            label="Email"
            value={client.email ?? '—'}
          />
          <InfoItem
            icon={<Calendar className="h-4 w-4" strokeWidth={1.5} />}
            label="Data di nascita"
            value={formatDate(client.dataNascita as unknown as string)}
          />
          <InfoItem
            icon={<User className="h-4 w-4" strokeWidth={1.5} />}
            label="Sesso"
            value={formatSesso(client.sesso)}
          />
          {client.codiceFiscale && (
            <InfoItem
              icon={<CreditCard className="h-4 w-4" strokeWidth={1.5} />}
              label="Codice fiscale"
              value={client.codiceFiscale}
            />
          )}
          {client.professione && (
            <InfoItem
              icon={<Briefcase className="h-4 w-4" strokeWidth={1.5} />}
              label="Professione"
              value={client.professione}
            />
          )}
          {(client.luogoNascita || client.provinciaNascita) && (
            <InfoItem
              icon={<MapPin className="h-4 w-4" strokeWidth={1.5} />}
              label="Luogo di nascita"
              value={[client.luogoNascita, client.provinciaNascita]
                .filter(Boolean)
                .join(' (') + (client.provinciaNascita ? ')' : '')}
            />
          )}
          {client.fonte && (
            <InfoItem
              icon={<ExternalLink className="h-4 w-4" strokeWidth={1.5} />}
              label="Fonte"
              value={client.fonte}
            />
          )}
        </div>

        {/* Indirizzo (solo se popolato) */}
        {(client.indirizzoVia || client.indirizzoCitta) && (
          <div className="mt-3 p-3 rounded-[var(--radius-md)] bg-surface-sunken border border-border-default">
            <div className="flex items-start gap-2">
              <MapPin className="h-4 w-4 mt-0.5 text-text-secondary shrink-0" strokeWidth={1.5} />
              <div className="min-w-0">
                <p className="text-xs text-text-muted mb-0.5">Indirizzo</p>
                <p className="text-sm text-text-primary">
                  {[
                    client.indirizzoVia,
                    [client.indirizzoCap, client.indirizzoCitta].filter(Boolean).join(' '),
                    client.indirizzoProvincia && `(${client.indirizzoProvincia})`,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* ---- Memo storici (ultimi 50 dal gestionale) ---- */}
      {isExternal && (
        <section className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="flex items-center gap-2 font-[family-name:var(--font-heading)] text-sm font-semibold text-text-secondary">
              <ScrollText className="h-4 w-4" strokeWidth={2} />
              Memo storici
              <Badge status="info" size="sm">dal gestionale</Badge>
            </h3>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => { setMemoText(''); setMemoError(null); setShowMemoModal(true); }}
            >
              + Memo
            </Button>
          </div>
          {client.extraMemo ? (
            <div className="p-4 rounded-[var(--radius-md)] bg-surface-sunken border border-border-default max-h-64 overflow-y-auto whitespace-pre-wrap text-xs text-text-secondary font-mono leading-relaxed">
              {client.extraMemo}
            </div>
          ) : (
            <p className="text-sm text-text-muted italic px-1">
              Nessuna memo registrata. Clicca "+ Memo" per aggiungerne una al gestionale.
            </p>
          )}
        </section>
      )}

      {/* ---- Note interne dal gestionale ---- */}
      {client.noteInterne && (
        <section className="mb-6">
          <h3 className="flex items-center gap-2 font-[family-name:var(--font-heading)] text-sm font-semibold text-text-secondary mb-3">
            <FileText className="h-4 w-4" strokeWidth={2} />
            Note dal gestionale
            <Badge status="info" size="sm">sincronizzato</Badge>
          </h3>
          <div className="p-4 rounded-[var(--radius-md)] bg-amber-50 border border-amber-200 whitespace-pre-wrap text-sm text-text-primary leading-relaxed">
            {client.noteInterne}
          </div>
        </section>
      )}

      {/* ---- Note esterne dal gestionale (raramente popolate) ---- */}
      {client.noteEsterne && (
        <section className="mb-6">
          <h3 className="flex items-center gap-2 font-[family-name:var(--font-heading)] text-sm font-semibold text-text-secondary mb-3">
            <FileText className="h-4 w-4" strokeWidth={2} />
            Note esterne
            <Badge status="info" size="sm">sincronizzato</Badge>
          </h3>
          <div className="p-4 rounded-[var(--radius-md)] bg-blue-50 border border-blue-200 whitespace-pre-wrap text-sm text-text-primary leading-relaxed">
            {client.noteEsterne}
          </div>
        </section>
      )}

      {/* ---- Prodotti acquistati (da dbgym RVW_VenditeMovimentiUtenti) ---- */}
      {isExternal && client.prodottiAcquistati && client.prodottiAcquistati.length > 0 && (
        <PurchasedProductsWidget items={client.prodottiAcquistati} formatDate={formatDate} />
      )}

      {/* ---- Consent badges ---- */}
      <section className="mb-6">
        <h3 className="font-[family-name:var(--font-heading)] text-sm font-semibold text-text-secondary mb-3">
          Consensi
        </h3>
        <div className="flex flex-wrap gap-2">
          {(
            ['trattamento_dati', 'foto', 'laser', 'marketing'] as ConsentType[]
          ).map((type) => {
            const granted = getConsentStatus(type);
            return (
              <button
                key={type}
                className={cn(
                  'inline-flex items-center gap-1.5',
                  'px-3 py-2 rounded-[var(--radius-full)]',
                  'text-sm font-[family-name:var(--font-heading)] font-semibold',
                  'border transition-all duration-[var(--duration-fast)]',
                  'min-h-[var(--touch-min)] cursor-pointer',
                  granted
                    ? 'bg-status-positive-light text-status-positive border-status-positive/20'
                    : 'bg-status-negative-light text-status-negative border-status-negative/20',
                  'hover:shadow-sm active:scale-[0.97]',
                )}
                aria-label={`Consenso ${CONSENT_LABELS[type]}: ${granted ? 'concesso' : 'non concesso'}`}
              >
                {granted ? (
                  <ShieldCheck className="h-4 w-4" strokeWidth={2} />
                ) : (
                  <ShieldX className="h-4 w-4" strokeWidth={2} />
                )}
                {CONSENT_LABELS[type]}
              </button>
            );
          })}
        </div>
      </section>

      {/* ---- New check-up CTA ---- */}
      <section className="mb-8">
        <Button
          size="lg"
          fullWidth
          leftIcon={<Plus strokeWidth={2} />}
          onClick={() => setShowNewCheckupModal(true)}
        >
          Nuovo Check-up
        </Button>
      </section>

      {/* ---- Timeline ---- */}
      <section>
        <h3 className="font-[family-name:var(--font-heading)] text-lg font-bold text-text-primary mb-4">
          Storico sessioni
        </h3>

        {timeline.length === 0 && (
          <div className="flex flex-col items-center py-12 text-center">
            <div
              className={cn(
                'mb-4 flex h-14 w-14 items-center justify-center',
                'rounded-full bg-surface-sunken',
              )}
            >
              <ClipboardCheck className="h-6 w-6 text-text-muted" strokeWidth={1.5} />
            </div>
            <p className="text-sm text-text-muted">
              Nessuna sessione registrata
            </p>
          </div>
        )}

        {timeline.length > 0 && (
          <div className="relative">
            {/* Vertical timeline line */}
            <div
              className="absolute left-5 top-0 bottom-0 w-px bg-border-default"
              aria-hidden
            />

            <div className="space-y-1">
              {timeline.map((entry) =>
                entry.kind === 'checkup' ? (
                  <TimelineCheckupCard
                    key={`checkup-${entry.data.id}`}
                    session={entry.data}
                    onTap={() => navigate(`/checkups/${entry.data.id}`)}
                    formatDate={formatDate}
                  />
                ) : (
                  <TimelineLaserCard
                    key={`laser-${entry.data.id}`}
                    session={entry.data}
                    formatDate={formatDate}
                  />
                ),
              )}
            </div>
          </div>
        )}
      </section>

      {/* Bottom spacer */}
      <div className="h-8" />

      {/* ---- New check-up bottom sheet / modal ---- */}
      {showNewCheckupModal && (
        <NewCheckupModal
          clientId={Number(id)}
          onClose={() => setShowNewCheckupModal(false)}
        />
      )}

      {/* ---- Add memo modal (push verso dbgym) ---- */}
      {showMemoModal && (
        <SimpleModal title="Aggiungi memo" onClose={() => setShowMemoModal(false)}>
          <p className="text-xs text-text-muted mb-3">
            La memo viene salvata su gestionale dbgym (Oggetto: <strong>MEMO CHECKUP</strong>) e appare subito qui.
          </p>
          <textarea
            autoFocus
            value={memoText}
            onChange={(e) => setMemoText(e.target.value)}
            placeholder="Es: Cliente interessata a percorso anti-età, propone follow-up tra 15 giorni…"
            rows={5}
            maxLength={2000}
            className="w-full px-3 py-2 rounded-[var(--radius-md)] border border-border-default bg-surface-card text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary"
          />
          <div className="flex items-center justify-between mt-1 mb-3 text-xs text-text-muted">
            <span>{memoText.length}/2000</span>
          </div>
          {memoError && (
            <p className="text-sm text-status-negative mb-3">{memoError}</p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="md" onClick={() => setShowMemoModal(false)}>
              Annulla
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={submitMemo}
              disabled={!memoText.trim() || memoSubmitting}
            >
              {memoSubmitting ? 'Salvataggio…' : 'Salva su gestionale'}
            </Button>
          </div>
        </SimpleModal>
      )}

      {/* ---- Edit anagrafica modal ---- */}
      {showEditModal && (
        <SimpleModal title="Modifica cliente" onClose={() => setShowEditModal(false)}>
          <p className="text-xs text-text-muted mb-3">
            Modifiche locali. Nota: il sync giornaliero da dbgym può sovrascrivere campi anagrafici.
            <br />
            <strong>Read-only</strong>: data nascita, codice fiscale, luogo nascita, ID gestionale.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <EditField label="Nome" value={editForm.nome ?? ''} onChange={(v) => setEditForm({ ...editForm, nome: v })} />
            <EditField label="Cognome" value={editForm.cognome ?? ''} onChange={(v) => setEditForm({ ...editForm, cognome: v })} />
            <EditField label="Telefono" value={editForm.telefono ?? ''} onChange={(v) => setEditForm({ ...editForm, telefono: v })} />
            <EditField label="Email" value={editForm.email ?? ''} onChange={(v) => setEditForm({ ...editForm, email: v })} type="email" />
            <EditField label="Professione" value={editForm.professione ?? ''} onChange={(v) => setEditForm({ ...editForm, professione: v })} />
            <EditField label="Fonte" value={editForm.fonte ?? ''} onChange={(v) => setEditForm({ ...editForm, fonte: v })} />
            <EditField label="Indirizzo" value={editForm.indirizzoVia ?? ''} onChange={(v) => setEditForm({ ...editForm, indirizzoVia: v })} className="col-span-2" />
            <EditField label="Città" value={editForm.indirizzoCitta ?? ''} onChange={(v) => setEditForm({ ...editForm, indirizzoCitta: v })} />
            <EditField label="CAP" value={editForm.indirizzoCap ?? ''} onChange={(v) => setEditForm({ ...editForm, indirizzoCap: v })} />
            <EditField label="Provincia (sigla)" value={editForm.indirizzoProvincia ?? ''} onChange={(v) => setEditForm({ ...editForm, indirizzoProvincia: v.toUpperCase().slice(0, 2) })} />
            <div className="col-span-2 flex flex-col gap-1.5">
              <label className="text-xs text-text-secondary">Note interne</label>
              <textarea
                value={editForm.noteInterne ?? ''}
                onChange={(e) => setEditForm({ ...editForm, noteInterne: e.target.value })}
                rows={4}
                className="px-3 py-2 rounded-[var(--radius-md)] border border-border-default bg-surface-card text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary"
              />
            </div>
            <div className="col-span-2 flex gap-3 flex-wrap">
              {[
                ['SMS', 'autorizzaSms'],
                ['Email', 'autorizzaEmail'],
                ['Cartaceo', 'autorizzaCartaceo'],
              ].map(([label, key]) => (
                <label key={key} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={Boolean(editForm[key as keyof ClientDetail])}
                    onChange={(e) => setEditForm({ ...editForm, [key]: e.target.checked })}
                  />
                  Consenso {label}
                </label>
              ))}
            </div>
          </div>
          {editError && <p className="text-sm text-status-negative mt-3">{editError}</p>}
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="secondary" size="md" onClick={() => setShowEditModal(false)}>
              Annulla
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={submitEdit}
              disabled={editSubmitting}
            >
              {editSubmitting ? 'Salvataggio…' : 'Salva'}
            </Button>
          </div>
        </SimpleModal>
      )}
    </AppShell>
  );
}

/* --------------------------------------------------------------------------
   Modal generico (overlay + card)
   -------------------------------------------------------------------------- */

function SimpleModal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-[var(--z-modal,50)] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-surface-card rounded-[var(--radius-lg)] shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-border-default">
          <h3 className="font-[family-name:var(--font-heading)] text-lg font-bold text-text-primary">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Chiudi"
            className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-surface-sunken cursor-pointer"
          >
            <X className="h-4 w-4 text-text-secondary" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

function EditField({
  label,
  value,
  onChange,
  type = 'text',
  className,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label className="text-xs text-text-secondary">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="px-3 py-2 rounded-[var(--radius-md)] border border-border-default bg-surface-card text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
      />
    </div>
  );
}

/* --------------------------------------------------------------------------
   Sub-components
   -------------------------------------------------------------------------- */

function PurchasedProductsWidget({
  items,
  formatDate,
}: {
  items: PurchasedProduct[];
  formatDate: (d: string) => string;
}) {
  const formatEuro = (v: number | null | undefined): string => {
    if (v === null || v === undefined || isNaN(v)) return '—';
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(v);
  };

  const totale = items.reduce(
    (sum, p) => sum + (p.prezzo ?? 0) * (p.quantita ?? 1),
    0,
  );

  return (
    <section className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="flex items-center gap-2 font-[family-name:var(--font-heading)] text-sm font-semibold text-text-secondary">
          <ShoppingBag className="h-4 w-4" strokeWidth={2} />
          Prodotti acquistati
          <Badge status="info" size="sm">dal gestionale</Badge>
        </h3>
        <span className="text-xs text-text-muted">
          {items.length} {items.length === 1 ? 'prodotto' : 'prodotti'} · totale {formatEuro(totale)}
        </span>
      </div>
      <div className="rounded-[var(--radius-md)] border border-border-default bg-surface-card max-h-80 overflow-y-auto divide-y divide-border-default">
        {items.map((p, i) => (
          <div key={i} className="flex items-start gap-3 p-3">
            <div className="flex h-9 w-9 items-center justify-center shrink-0 rounded-[var(--radius-md)] bg-accent-primary-light">
              <ShoppingBag className="h-4 w-4 text-accent-primary" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-text-primary truncate">
                {p.prodotto}
              </p>
              <p className="text-xs text-text-muted mt-0.5">
                {formatDate(p.data)}
                {p.categoria && ` · ${p.categoria}`}
                {p.operatore && ` · ${p.operatore}`}
              </p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-sm font-semibold text-text-primary">
                {formatEuro(p.prezzo)}
              </p>
              {p.quantita && p.quantita !== 1 && (
                <p className="text-xs text-text-muted">×{p.quantita}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function InfoItem({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 px-3 py-3',
        'rounded-[var(--radius-md)] bg-surface-card',
        'border border-border-default',
      )}
    >
      <span className="shrink-0 text-text-muted">{icon}</span>
      <div className="min-w-0">
        <p className="text-xs text-text-muted">{label}</p>
        <p className="text-sm font-medium text-text-primary truncate">{value}</p>
      </div>
    </div>
  );
}

function TimelineCheckupCard({
  session,
  onTap,
  formatDate,
}: {
  session: CheckupSession;
  onTap: () => void;
  formatDate: (d: string) => string;
}) {
  const Icon = SESSION_TYPE_ICONS[session.sessionType] ?? Sparkles;
  const label = SESSION_TYPE_LABELS[session.sessionType] ?? session.sessionType;

  return (
    <button
      onClick={onTap}
      className={cn(
        'relative flex w-full items-center gap-4 pl-12 pr-3 py-3',
        'text-left cursor-pointer',
        'rounded-[var(--radius-lg)]',
        'transition-all duration-[var(--duration-fast)]',
        'hover:bg-surface-card hover:shadow-xs',
        'active:bg-surface-sunken',
        'min-h-[var(--touch-comfortable)]',
      )}
    >
      {/* Timeline dot */}
      <div
        className={cn(
          'absolute left-2.5 top-1/2 -translate-y-1/2',
          'flex h-5 w-5 items-center justify-center',
          'rounded-full bg-accent-primary-light',
          'ring-2 ring-surface-base',
          'z-10',
        )}
      >
        <div className="h-2 w-2 rounded-full bg-accent-primary" />
      </div>

      {/* Icon */}
      <div
        className={cn(
          'flex h-10 w-10 items-center justify-center shrink-0',
          'rounded-[var(--radius-md)] bg-accent-primary-light',
        )}
      >
        <Icon className="h-5 w-5 text-accent-primary" strokeWidth={1.5} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="font-[family-name:var(--font-heading)] text-sm font-semibold text-text-primary">
          {label}
        </p>
        <p className="text-xs text-text-muted mt-0.5">
          {formatDate(session.createdAt)}
          {session.operatriceName && ` — ${session.operatriceName}`}
        </p>
      </div>

      {/* Status badge */}
      <Badge
        status={session.status === 'completed' ? 'positive' : 'info'}
        size="sm"
        dot
      >
        {session.status === 'completed' ? 'Completato' : 'In corso'}
      </Badge>

      <ChevronRight className="h-4 w-4 text-text-muted shrink-0" strokeWidth={2} />
    </button>
  );
}

function TimelineLaserCard({
  session,
  formatDate,
}: {
  session: LaserSession;
  formatDate: (d: string) => string;
}) {
  return (
    <div
      className={cn(
        'relative flex items-center gap-4 pl-12 pr-3 py-3',
        'rounded-[var(--radius-lg)]',
        'min-h-[var(--touch-comfortable)]',
      )}
    >
      {/* Timeline dot */}
      <div
        className={cn(
          'absolute left-2.5 top-1/2 -translate-y-1/2',
          'flex h-5 w-5 items-center justify-center',
          'rounded-full bg-accent-secondary-light',
          'ring-2 ring-surface-base',
          'z-10',
        )}
      >
        <div className="h-2 w-2 rounded-full bg-accent-secondary" />
      </div>

      {/* Icon */}
      <div
        className={cn(
          'flex h-10 w-10 items-center justify-center shrink-0',
          'rounded-[var(--radius-md)] bg-accent-secondary-light',
        )}
      >
        <Zap className="h-5 w-5 text-accent-secondary" strokeWidth={1.5} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="font-[family-name:var(--font-heading)] text-sm font-semibold text-text-primary">
          Seduta Laser #{session.sedutaNumber}
        </p>
        <p className="text-xs text-text-muted mt-0.5">
          {formatDate(session.createdAt)}
          {session.operatriceName && ` — ${session.operatriceName}`}
        </p>
        {session.zone && (
          <p className="text-xs text-text-secondary mt-0.5">
            Zone: {session.zone}
            {session.parametersSummary && ` — ${session.parametersSummary}`}
          </p>
        )}
      </div>

      <Badge status="neutral" size="sm" dot>
        Laser
      </Badge>
    </div>
  );
}

/* --------------------------------------------------------------------------
   New Checkup Modal (Bottom sheet)
   -------------------------------------------------------------------------- */

function NewCheckupModal({
  clientId,
  onClose,
}: {
  clientId: number;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  const handleSelect = async (sessionType: SessionType) => {
    setCreating(true);
    try {
      const result = await api.post<{ id: number }>('/checkups', {
        clientId,
        sessionType,
      });
      navigate(`/checkups/${result.id}`);
    } catch {
      // Fallback: navigate to the new-checkup page
      navigate(`/clients/${clientId}/new-checkup`);
    } finally {
      setCreating(false);
    }
  };

  const sessionTypes: SessionType[] = ['welcome', 'beauty', 'kinesiology', 'medical'];

  return (
    <>
      {/* Overlay */}
      <div
        className={cn(
          'fixed inset-0 z-[var(--z-overlay)] bg-surface-overlay',
          'fade-in',
        )}
        onClick={onClose}
        aria-hidden
      />

      {/* Sheet */}
      <div
        className={cn(
          'fixed bottom-0 left-0 right-0 z-[var(--z-modal)]',
          'bg-surface-card rounded-t-[var(--radius-xl)]',
          'shadow-xl',
          'px-5 pb-8 pt-5',
          'mx-auto max-w-3xl',
          'fade-in',
        )}
        role="dialog"
        aria-label="Seleziona tipo di check-up"
      >
        {/* Handle */}
        <div className="flex justify-center mb-4">
          <div className="h-1 w-10 rounded-full bg-border-default" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-[family-name:var(--font-heading)] text-lg font-bold text-text-primary">
            Nuovo Check-up
          </h3>
          <button
            onClick={onClose}
            className={cn(
              'flex items-center justify-center',
              'h-[var(--touch-min)] w-[var(--touch-min)]',
              'rounded-[var(--radius-md)]',
              'text-text-muted hover:text-text-primary',
              'hover:bg-surface-sunken',
              'transition-colors cursor-pointer',
            )}
            aria-label="Chiudi"
          >
            <X className="h-5 w-5" strokeWidth={2} />
          </button>
        </div>

        {/* Session type cards */}
        <div className="grid grid-cols-2 gap-3">
          {sessionTypes.map((type) => {
            const Icon = SESSION_TYPE_ICONS[type];
            return (
              <button
                key={type}
                onClick={() => handleSelect(type)}
                disabled={creating}
                className={cn(
                  'flex flex-col items-center gap-3',
                  'p-5 rounded-[var(--radius-lg)]',
                  'border border-border-default bg-surface-card',
                  'text-center cursor-pointer',
                  'transition-all duration-[var(--duration-normal)] ease-[var(--ease-out)]',
                  'hover:border-accent-primary hover:shadow-sm hover:-translate-y-0.5',
                  'active:translate-y-0 active:shadow-xs active:border-accent-primary',
                  'disabled:opacity-50 disabled:pointer-events-none',
                  'min-h-[120px]',
                )}
              >
                <div
                  className={cn(
                    'flex h-12 w-12 items-center justify-center',
                    'rounded-[var(--radius-md)] bg-accent-primary-light',
                  )}
                >
                  <Icon className="h-6 w-6 text-accent-primary" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-[family-name:var(--font-heading)] text-sm font-semibold text-text-primary">
                    {SESSION_TYPE_LABELS[type]}
                  </p>
                  <p className="text-xs text-text-muted mt-1">
                    {SESSION_TYPE_DESCRIPTIONS[type]}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
